import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  TextField,
  Box,
  Typography,
  Stack,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Tooltip
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import { addWorkerId, editWorkerId, deleteWorkerId, getAllWorkerIdentificationTypes, getEditWorkerIds } from '../../../../api/apiCall';
import { useAuth } from '../../../../auth/auth';
import { toast } from 'react-toastify';
import { Modal } from 'antd';

interface IdentificationModalProps {
  open: boolean;
  onClose: () => void;
  workerId: string;
  onSave?: (identifications: any[]) => void;
}

const WorkerIdenticationsType: React.FC<IdentificationModalProps> = ({
  open,
  onClose,
  workerId,
}) => {
  const { state } = useAuth();
  const [workerIdentifications, setWorkerIdentifications] = useState<any[]>([]);
  const [allIdentificationTypes, setAllIdentificationTypes] = useState<any[]>([]);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editTypeId, setEditTypeId] = useState('');
  const [editNumber, setEditNumber] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
    const [, setOpenDeleteModal] = useState(false);

     const resetAllState = () => {
    setWorkerIdentifications([]);
    setAllIdentificationTypes([]);
    setEditingId(null);
    setEditTypeId('');
    setEditNumber('');
    setIsLoading(false);
    setIsSaving(false);
  };

  const getWorkerIdentifications = async () => {
    try {
      const response = await getEditWorkerIds(state?.user?.token, workerId);
      console.log('Worker Identifications Response:', response);
      if (response && response.data) {
        setWorkerIdentifications(response.data.workerIds || []);
      }
    } catch (error) {
      console.error('Error fetching worker identifications:', error);
    }
  };

  const getAllIdentifications = async () => {
    try {
      const response = await getAllWorkerIdentificationTypes(state.user?.token);
      if (response && response.data) {
        setAllIdentificationTypes(response.data);
      }
    } catch (error) {
      console.error('Error fetching identification types:', error);
    }
  };

  useEffect(() => {
    if (open) {
      setIsLoading(true);
      getAllIdentifications();
      getWorkerIdentifications();
      setIsLoading(false);
    } else {
      resetAllState();
    }
  }, [open, workerId]);

  const handleEdit = (identification: any) => {
    setEditingId(identification.id);
    setEditTypeId(identification.identificationTypeId);
    setEditNumber(identification.number);
  };

  const handleSaveEdit = async () => {
    if (!editingId || !editTypeId || !editNumber) {
      toast.error('Please fill in all fields');
      return;
    }

    setIsSaving(true);
    
    const isNew = editingId.toString().startsWith('temp-');
    
    if (isNew) {
      const response = await addWorkerId(state.user?.token, {
        workerId,
        identificationTypeId: editTypeId,
        number: editNumber
      });
      
      if (response) {
        toast.success('Identification added successfully');
        await getWorkerIdentifications();
        setEditingId(null);
        setEditTypeId('');
        setEditNumber('');
      } else {
        toast.error('Failed to add identification');
      }
    } else {
      const response = await editWorkerId(state.user?.token, {
        workerId,
        id: editingId,
        identificationTypeId: editTypeId,
        number: editNumber
      });
      
      if (response) {
        toast.success('Identification updated successfully');
        await getWorkerIdentifications();
        setEditingId(null);
        setEditTypeId('');
        setEditNumber('');
      } else {
        toast.error('Failed to update identification');
      }
    }
    
    setIsSaving(false);
  };

  const handleCancelEdit = () => {
    if (editingId && editingId.toString().startsWith('temp-')) {
      setWorkerIdentifications(prev => prev.filter(ident => ident.id !== editingId));
    }
    setEditingId(null);
    setEditTypeId('');
    setEditNumber('');
  };

  const handleDelete = async (id: string) => {
    if (id.toString().startsWith('temp-')) {
      setWorkerIdentifications(prev => prev.filter(ident => ident.id !== id));
      toast.info('Removed unsaved identification');
    } else {
        const response = await deleteWorkerId(state.user?.token, {
          workerId,
          id
        });
        
        if (response) {
          toast.success('Identification deleted successfully');
          await getWorkerIdentifications();
        } else {
          toast.error('Failed to delete identification');
        }
    }
  };

  const handleAddNew = () => {
    if (editingId && editingId.toString().startsWith('temp-')) {
      toast.warning('Please save or cancel the current new identification first');
      return;
    }
    
    const newId = `temp-${Date.now()}`;
    const newIdentification = {
      id: newId,
      number: '',
      identificationTypeId: '',
    };
    setWorkerIdentifications(prev => [newIdentification, ...prev]);
    setEditingId(newId);
  };

  const getIdentificationTypeName = (typeId: string) => {
    const type = allIdentificationTypes.find(t => t.id === typeId);
    return type ? type.name : 'Unknown';
  };

  const handleClose = () => {
    resetAllState();
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            Manage Identifications
            <IconButton
              onClick={handleAddNew}
              size="small"
              sx={{ ml: 1 }}
              disabled={isLoading || isSaving}
            >
              <AddIcon />
            </IconButton>
          </Typography>
          <IconButton onClick={onClose} size="small" disabled={isSaving}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        {isLoading ? (
          <Box textAlign="center" py={4}>
            <CircularProgress size={40} />
            <Typography sx={{ mt: 2 }}>Loading identifications...</Typography>
          </Box>
        ) : (
          <Stack spacing={1}>
            {workerIdentifications.map((identification) => (
              <Box
                key={identification.id}
                sx={{
                  p: 1.5,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  border: '1px solid #e0e0e0',
                  borderRadius: 1,
                // bgcolor: editingId === identification.id ? '#f5f5f5' : 'white',
                  opacity: isSaving && editingId === identification.id ? 0.7 : 1
                }}
              >
                {editingId === identification.id ? (
                  <>
                    <FormControl size="small" sx={{ flex: 1 }} required>
                      <InputLabel>Type</InputLabel>
                      <Select
                        value={editTypeId}
                        onChange={(e) => setEditTypeId(e.target.value)}
                        label="Type"
                        disabled={isSaving}
                      >
                        <MenuItem value="" disabled>Select identification type</MenuItem>
                        {allIdentificationTypes.map((type) => (
                          <MenuItem key={type.id} value={type.id}>
                            <Tooltip title={type.description} placement="right">
                              <span style={{ width: '100%', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {type.name}
                              </span>
                            </Tooltip>
                        </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <TextField
                      label="Number"
                      size="small"
                      value={editNumber}
                      onChange={(e) => setEditNumber(e.target.value)}
                      sx={{ flex: 2 }}
                      placeholder="Enter identification number"
                      disabled={isSaving}
                      required
                    />
                    <Button 
                      size="small" 
                      onClick={handleCancelEdit}
                      disabled={isSaving}
                    >
                      Cancel
                    </Button>
                    <Button 
                      size="small" 
                      variant="contained" 
                      onClick={handleSaveEdit} 
                      style={{ boxShadow: 'none' }}
                      disabled={isSaving || !editTypeId || !editNumber}
                    >
                      {isSaving ? <CircularProgress size={20} /> : 'Save'}
                    </Button>
                  </>
                ) : (
                  <>
                    <Typography sx={{ flex: 1, fontWeight: 500 }}>
                      {getIdentificationTypeName(identification.identificationTypeId)}
                    </Typography>
                    <Typography sx={{ flex: 2 }} color="textSecondary">
                      {identification.number}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => handleEdit(identification)}
                      color="primary"
                      disabled={isSaving}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => {
                            setOpenDeleteModal(true);
                            Modal.confirm({
                                title: `Are you sure you want to delete this identification?`,
                                onOk: () => handleDelete(identification.id),
                                onCancel: () => setOpenDeleteModal(false),
                                okButtonProps: { danger: true },
                                okText: 'YES',
                                cancelText: 'NO',
                                centered: true
                            });
                        }}
                      color="error"
                      disabled={isSaving}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </>
                )}
              </Box>
            ))}

            {workerIdentifications.length === 0 && (
              <Box textAlign="center" py={4}>
                <Typography color="textSecondary">
                  No identifications added. Click the + button to add one.
                </Typography>
              </Box>
            )}
          </Stack>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={isSaving}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default WorkerIdenticationsType;
